import axios from "axios";
import { appConfig } from "@/config/appConfig";

let isLoggingOut = false;

function safeCommit(store, mutation, payload) {
    try {
        if (typeof payload === "undefined") {
            store.commit(mutation);
            return;
        }

        store.commit(mutation, payload);
    } catch {
        // ignore if mutation does not exist
    }
}

function cleanupStore(store) {
    safeCommit(store, "SET_AUTH_LOGOUT");
    safeCommit(store, "CLEAR_CONTRACT_NOTIF");
    safeCommit(store, "CLEAR_ITEMS_CONTRACT", []);
    safeCommit(store, "CLEAR_CONTRACT_HEADER");
    safeCommit(store, "CLEAR_ITEMS_RECEIVING", []);
    safeCommit(store, "CLEAR_RECEIVING_HEADER");
}

function cleanupStorage(logoutMessage) {
    try {
        sessionStorage.clear();
        sessionStorage.setItem("logout_message", logoutMessage);
    } catch {
        // ignore storage error
    }
}

function isNetworkError(error) {
    const code = error?.code;
    const status = error?.response?.status;

    if (status) {
        return false;
    }

    return [
        "ERR_NETWORK",
        "ECONNABORTED",
        "ETIMEDOUT",
        "ENOTFOUND",
        "EAI_AGAIN",
        "ECONNRESET",
        "EHOSTUNREACH",
    ].includes(code) ||
        /network error|timeout|request failed/i.test(error?.message || "");
}

function getPathname(url) {
    if (!url || typeof url !== "string") {
        return "";
    }

    try {
        if (/^https?:\/\//i.test(url)) {
            return new URL(url).pathname || "";
        }
    } catch {
        // fall through
    }

    const withoutHash = url.split("#")[0];
    return withoutHash.split("?")[0] || "";
}

function pathAlreadyHasToken(url, token) {
    if (!url || !token) {
        return false;
    }

    const pathname = getPathname(url);
    const raw = String(token);
    const encoded = encodeURIComponent(raw);

    // Token may contain `/`, `@`, space — check as substring, not only last segment.
    if (
        pathname.includes(`/${raw}`) ||
        pathname.includes(`/${encoded}`) ||
        pathname.endsWith(raw) ||
        pathname.endsWith(encoded)
    ) {
        return true;
    }

    try {
        const decoded = decodeURIComponent(pathname);
        if (decoded.includes(`/${raw}`) || decoded.endsWith(raw)) {
            return true;
        }
    } catch {
        // ignore decode error
    }

    // Manual token may be stale (component.authToken) while store has a new one.
    // If path already ends with a long token-like segment, do not append again.
    const segments = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
    const last = segments[segments.length - 1] || "";

    if (last.length >= 40) {
        return true;
    }

    // Token with embedded `/` becomes multiple segments, e.g. .../MobTz/8bpQpul...
    if (segments.length >= 2) {
        const prev = segments[segments.length - 2] || "";
        if (prev.length >= 40 && last.length >= 8) {
            return true;
        }
    }

    return false;
}

export function setupAxios({ store, router, toast }) {
    axios.defaults.baseURL = appConfig.baseUrl;
    axios.defaults.timeout = 120000;

    axios.interceptors.request.use(
        (config) => {
            if (config.skipAuthInterceptor) {
                return config;
            }

            try {
                const token = store.getters.GET_AUTH_TOKEN;
                const url = config.url;

                if (
                    token &&
                    typeof url === "string" &&
                    appConfig.bff &&
                    !url.startsWith(appConfig.bff)
                ) {
                    const tokenSegment = encodeURIComponent(token);
                    const hashIndex = url.indexOf("#");
                    const hash = hashIndex >= 0 ? url.substring(hashIndex) : "";
                    const urlWithoutHash = hashIndex >= 0 ? url.substring(0, hashIndex) : url;
                    const queryIndex = urlWithoutHash.indexOf("?");
                    const path = queryIndex >= 0 ? urlWithoutHash.substring(0, queryIndex) : urlWithoutHash;
                    const query = queryIndex >= 0 ? urlWithoutHash.substring(queryIndex) : "";
                    const insertBeforeLastSegment = Boolean(config.insertTokenBeforeLastPathSegment);

                    if (!pathAlreadyHasToken(url, token)) {
                        if (insertBeforeLastSegment) {
                            const normalizedPath = path.replace(/\/+$/, "");
                            const lastSlashIndex = normalizedPath.lastIndexOf("/");

                            if (lastSlashIndex > -1) {
                                const basePath = normalizedPath.substring(0, lastSlashIndex);
                                const lastSegment = normalizedPath.substring(lastSlashIndex + 1);

                                config.url = `${basePath}/${tokenSegment}/${lastSegment}${query}${hash}`;
                            } else {
                                config.url = `${normalizedPath}/${tokenSegment}${query}${hash}`;
                            }
                        } else {
                            config.url = `${path}/${tokenSegment}${query}${hash}`;
                        }
                    }
                }
            } catch {
                // ignore errors when reading token
            }

            return config;
        },
        (error) => Promise.reject(error)
    );

    axios.interceptors.response.use(
        (response) => response,

        async (error) => {
            const config = error.config || {};

            if (config.skipAuthInterceptor) {
                return Promise.reject(error);
            }

            const status = error?.response?.status;

            if (isNetworkError(error)) {
                try {
                    toast?.open({
                        message: "Tidak bisa terhubung ke server. Silakan cek koneksi Anda.",
                        type: "warning",
                        duration: 4000,
                    });
                } catch {
                    // ignore toast error
                }

                return Promise.reject(error);
            }

            if (status === 401 && !isLoggingOut) {
                const hasToken = Boolean(store.getters.GET_AUTH_TOKEN);

                if (!hasToken) {
                    return Promise.reject(error);
                }

                isLoggingOut = true;

                const logoutMessage =
                    error?.response?.data?.message ||
                    "Session expired. Please login again.";

                try {
                    toast?.open({
                        message: logoutMessage,
                        type: "error",
                        duration: 5000,
                    });
                } catch {
                    // ignore toast error
                }

                cleanupStore(store);
                cleanupStorage(logoutMessage);

                try {
                    if (router.currentRoute?.value?.name !== "login") {
                        router.replace({
                            name: "login",
                            query: {
                                reason: "session-expired",
                            },
                        });
                    }
                } catch {
                    // ignore router error
                }

                setTimeout(() => {
                    isLoggingOut = false;
                }, 1000);
            }

            return Promise.reject(error);
        }
    );
}
